import React, { useState, useEffect } from "react";
import { X, Youtube, Instagram, Linkedin, AlertCircle, CheckCircle, Loader } from "lucide-react";
import { useForm, Controller, set } from "react-hook-form";
import axios from "axios";

const VideoSubmitModal = ({ setShowSubmitModal, selectedVideo, onSubmit, setVideos, fetchVideos }) => {
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [loading, setLoading] = useState(false);

  const durationRange = [
    "0-90s",
    "91s-3m",
    "3-5min",
    "5-10min",
    "10min+",
  ];

  // Platform-specific validation and UI config
  const platformConfig = {
    youtube: {
      name: "YouTube",
      icon: <Youtube className="h-5 w-5" />,
      color: "text-red-600",
      bgColor: "bg-red-100",
      borderColor: "border-red-200",
      hoverColor: "hover:bg-red-200",
      allowedRanges: durationRange,
      placeholder: "Enter YouTube video URL (e.g., https://youtube.com/watch?v=...)",
      urlPattern: /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/).+/
    },
    instagram: {
      name: "Instagram",
      icon: <Instagram className="h-5 w-5" />,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      borderColor: "border-purple-200",
      hoverColor: "hover:bg-purple-200",
      allowedRanges: ["0-90s"],
      placeholder: "Enter Instagram video URL (e.g., https://instagram.com/p/...)",
      urlPattern: /^(https?:\/\/)?(www\.)?instagram\.com\/.+/
    },
    linkedin: {
      name: "LinkedIn",
      icon: <Linkedin className="h-5 w-5" />,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      borderColor: "border-blue-200",
      hoverColor: "hover:bg-blue-200",
      allowedRanges: durationRange.filter((range) => range !== "10min+"),
      placeholder: "Enter LinkedIn video URL (e.g., https://linkedin.com/posts/...)",
      urlPattern: /^(https?:\/\/)?(www\.)?linkedin\.com\/.+/
    }
  };

  // Determine available platforms based on duration range
  useEffect(() => {
    const currentRange = selectedVideo?.storyType?.durationRange || "";
    const platforms = [];

    if (platformConfig.youtube.allowedRanges.includes(currentRange)) {
      platforms.push("youtube");
    }

    if (platformConfig.instagram.allowedRanges.includes(currentRange)) {
      platforms.push("instagram");
    }

    if (platformConfig.linkedin.allowedRanges.includes(currentRange)) {
      platforms.push("linkedin");
    }

    setAvailablePlatforms(platforms);
  }, [selectedVideo?.storyType?.durationRange]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      platforms: {
        youtube: { url: "" },
        instagram: { url: "" },
        linkedin: { url: "" }
      }
    },
  });

  const onFormSubmit = async (data) => {
    try {
      setLoading(true);
      const validLinks = [];

      Object.entries(data.platforms).forEach(([platform, { url }]) => {
        if (url && availablePlatforms.includes(platform)) {
          validLinks.push(url);
        }
      });

      if (validLinks.length === 0) {
        alert("Please provide at least one valid video URL");
        return;
      }

      const formattedData = {
        videoId: selectedVideo._id,
        videos: {
          status: "VIDEO_SUBMITTED",
          videoLinks: validLinks
        },
      };


      await axios.put(
        `${process.env.REACT_APP_API_URL}/update-video`,
        formattedData,
        {
          withCredentials: true,
        }
      );

      alert("Video links submitted successfully");

      setShowSubmitModal(false);
      const response = await fetchVideos();
      setVideos(response.data);

      if (onSubmit) onSubmit();
      setLoading(false);
    } catch (error) {
      console.error("Error submitting video links:", error);
      alert("Failed to submit video links. Please try again.");
      setLoading(false);
    }
  };

  const currentRange = selectedVideo?.storyType?.durationRange || "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={() => setShowSubmitModal(false)}
      />

      {/* Modal */}
      <div className="relative w-full max-w-[60%] rounded-lg bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {selectedVideo?.status === "SUBMISSION_NOT_ACCEPTED" ? "Re-submit Video: " : ""}{selectedVideo?.topic} —— Published Date: {new Date(
              selectedVideo?.phases?.find(p => p.name === "PUBLISH")?.endDate
            ).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric"
            })}
          </h3>
          <button
            onClick={() => setShowSubmitModal(false)}
            className="rounded-full p-1 hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Re-submission notification */}
        {selectedVideo?.status === "SUBMISSION_NOT_ACCEPTED" && (
          <div className="mb-4 rounded-md bg-yellow-50 p-3 border border-yellow-200">
            <div className="flex items-start">
              <AlertCircle className="mr-2 h-5 w-5 text-yellow-600 mt-1" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Re-submission Open</p>
              </div>
            </div>
          </div>
        )}

        {/* Current video duration info */}
        {currentRange && (
          <div className="mb-4 rounded-md bg-gray-100 p-3">
            {selectedVideo?.phases?.some(p => p.name === "PUBLISH") && (
              <div className="mb-4 rounded-md bg-red-50 p-3 border border-red-200">
                <div className="flex items-start">
                  <AlertCircle className="mr-2 h-5 w-5 text-red-500 mt-1" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium">
                      Submission Note
                    </p>
                    <p className="mt-1">
                      <strong>{selectedVideo?.topic}</strong> must be published <strong>exactly on{" "}
                        {new Date(
                          selectedVideo?.phases?.find(p => p.name === "PUBLISH")?.endDate
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric"
                        })}</strong> and meet the required viewership criteria YouTube to be accepted.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-start">
              <AlertCircle className="mr-2 h-5 w-5 text-blue-500" />
              <div className="text-sm">
                <p className="font-medium text-gray-700">
                  Current Video Duration: <span className="font-bold">{currentRange}</span>
                </p>
                <p className="mt-1 text-gray-600">
                  Based on this duration, you can submit to the following platforms:
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {/* Show fields based on available platforms */}
          {availablePlatforms.length === 0 ? (
            <div className="rounded-md bg-yellow-100 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">No platforms available</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      The current video duration ({currentRange}) is not compatible with any supported platforms.
                      Please select a different video or adjust the duration range.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            availablePlatforms.map((platform) => (
              <div key={platform} className={`rounded-md border ${platformConfig[platform].borderColor} p-4`}>
                <label className={`mb-2 block text-sm font-medium ${platformConfig[platform].color}`}>
                  {platformConfig[platform].name} Video URL <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center">
                  <div className={`mr-2 ${platformConfig[platform].bgColor} rounded-full p-1`}>
                    {platformConfig[platform].icon}
                  </div>
                  <input
                    type="url"
                    {...register(`platforms.${platform}.url`, {
                      required: `${platformConfig[platform].name} URL is required`,
                      validate: {
                        pattern: (val) =>
                          platformConfig[platform].urlPattern.test(val) ||
                          `Must be a valid ${platformConfig[platform].name} URL`,
                        noQuery: (val) =>
                          !val.includes("?") || "Query parameters are not allowed in the URL",
                      }
                    })}
                    placeholder={platformConfig[platform].placeholder}
                    className={`w-full rounded-md border ${errors.platforms?.[platform]?.url ? "border-red-500" : "border-gray-300"
                      } px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                  />
                </div>
                {errors.platforms?.[platform]?.url && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.platforms[platform].url.message}
                  </p>
                )}
                <div className="mt-2 text-xs text-gray-500">
                  <CheckCircle className="mr-1 inline h-3 w-3" />
                  Supports videos with duration: {platformConfig[platform].allowedRanges.join(", ")}
                </div>
              </div>
            ))
          )}

          {/* Action buttons */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowSubmitModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={availablePlatforms.length === 0 || loading}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${availablePlatforms.length > 0
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-400 cursor-not-allowed"
                }`}
            >
              {loading ?
                <span className="flex items-center">
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </span> :
                "Submit"
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VideoSubmitModal;
